import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createApiClient } from '../client';
import { useAuth } from '../../auth/useAuth';

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

export type ConflictType =
  | 'internal'
  | 'interpersonal'
  | 'societal'
  | 'environmental'
  | 'supernatural';

export type ResolutionStatus =
  | 'unresolved'
  | 'partial'
  | 'resolved'
  | 'escalated';

export interface ConflictDto {
  id: string;
  sessionId?: string | null;
  outlineId?: string | null;
  conflictType?: ConflictType | null;
  title: string;
  description?: string | null;
  charactersInvolved: string[];
  resolutionStatus?: ResolutionStatus | null;
  updatedAt: string;
}

export function useConflicts(params: {
  sessionId?: string;
  outlineId?: string;
}) {
  const { getToken } = useAuth();
  const enabled = !!params.sessionId || !!params.outlineId;
  return useQuery<ConflictDto[]>({
    enabled,
    queryKey: ['conflicts', params.sessionId, params.outlineId],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const searchParams = new URLSearchParams();
      if (params.sessionId) searchParams.set('sessionId', params.sessionId);
      if (params.outlineId) searchParams.set('outlineId', params.outlineId);
      const resp = await client
        .get(`api/v2/conflicts?${searchParams.toString()}`)
        .json<ApiEnvelope<ConflictDto[]>>();
      return resp.data ?? [];
    },
  });
}

export function useCreateConflict() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      sessionId?: string | null;
      outlineId?: string | null;
      conflictType?: string | null;
      title: string;
      description?: string | null;
      charactersInvolved?: string[];
      resolutionStatus?: string | null;
    }) => {
      const client = createApiClient(getToken);
      const resp = await client
        .post('api/v2/conflicts', { json: input })
        .json<ApiEnvelope<ConflictDto>>();
      return resp.data;
    },
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({
        queryKey: ['conflicts', vars.sessionId, vars.outlineId],
      });
    },
  });
}

export function useUpdateConflict() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      conflictType?: string | null;
      title: string;
      description?: string | null;
      charactersInvolved?: string[];
      resolutionStatus?: string | null;
    }) => {
      const { id, ...body } = input;
      const client = createApiClient(getToken);
      const resp = await client
        .put(`api/v2/conflicts/${id}`, { json: body })
        .json<ApiEnvelope<ConflictDto>>();
      return resp.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conflicts'] });
    },
  });
}

export function useDeleteConflict() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const client = createApiClient(getToken);
      await client.delete(`api/v2/conflicts/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conflicts'] });
    },
  });
}
